import { useEffect } from 'react';
import {
  applyFormatters,
  FormatterOptions,
  jsonEditorWidgets,
  ReadonlyFormattingPlugin,
} from '@asset-metrix/portal-ui';
import {
  JsonError,
  JsonSchema,
  useEditor,
  useEditorPlugin,
  ValueNode,
  Widget,
} from '@sagold/react-json-editor';
import clsx from 'clsx';

import { formattersByType } from '../table/configToColDef';

export type WorkflowActionModalInputEditorProps = {
  data: Record<string, unknown>;
  disabled: boolean;
  readOnly?: boolean;
  formattingOptions?: Record<string, FormatterOptions | undefined>;
  onChange?: (data: Record<string, unknown>, errors: JsonError[]) => void;
  schema: JsonSchema;
};

function getFormattingOptionsForNode(
  node: ValueNode,
  formattingOptions?: Record<string, FormatterOptions | undefined>
): FormatterOptions {
  // In case there are nested objects in the schema, always use the "root"
  // property to look up the formatting options.
  const formattingOptionProperty = node.pointer.replace('#/', '').split('/')[0];
  const result =
    formattingOptionProperty != null
      ? formattingOptions?.[formattingOptionProperty]
      : undefined;
  return result ?? {};
}

export function WorkflowActionModalInputEditor({
  data,
  disabled,
  readOnly = false,
  formattingOptions,
  onChange,
  schema,
}: WorkflowActionModalInputEditorProps) {
  const editor = useEditor({
    data,
    schema: { ...schema, description: undefined },
    widgets: jsonEditorWidgets,
    validate: !readOnly,
    onChange: (data, _root, editor) => {
      onChange?.(data, readOnly ? [] : editor.getErrors());
    },
  });

  useEditorPlugin(editor, ReadonlyFormattingPlugin, {
    stringFormatter: (node) => {
      return (
        applyFormatters(
          formattersByType.text,
          node.value,
          getFormattingOptionsForNode(node, formattingOptions)
        ) ?? undefined
      );
    },
    dateFormatter: (node) => {
      return (
        applyFormatters(
          formattersByType.date,
          node.value,
          getFormattingOptionsForNode(node, formattingOptions)
        ) ?? undefined
      );
    },
    numberFormatter: (node) => {
      return (
        applyFormatters(
          formattersByType.number,
          node.value,
          getFormattingOptionsForNode(node, formattingOptions)
        ) ?? undefined
      );
    },
  });

  // When there is only one editable, required field, the validation is not running
  // for that field, causing it to not throw any validation error even if it is empty.
  // => Execute the validation "manually" upfront. Skip in readonly mode — we only display data.
  useEffect(() => {
    if (!readOnly) {
      editor?.validate();
    }
  }, [editor, readOnly]);

  return (
    <div className={clsx('portal-form rje-form rje-theme rje-theme--light')}>
      <Widget editor={editor} options={{ disabled, readOnly }} />
    </div>
  );
}
